import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, adminApi, type AdminProposalDetail } from '../lib/api';
import type { ProposalDocV1 } from '../../worker/render/types';
import { useAutosave } from '../lib/useAutosave';
import { relativeTime } from '../lib/format';
import StatusChip from '../components/StatusChip';
import { HeroSection, ChallengeSection, SolutionSection, MobileStory } from '../components/editor/NarrativeSections';
import PackagesSection from '../components/editor/PackagesSection';
import { PricingSection, TermsSection } from '../components/editor/CommercialSections';
import { FaqSection, TestimonialsSection, TimelineSection, ReferenceVideoSection, LinksSection } from '../components/editor/ExtrasSections';
import { SignedLockBanner, ResetSignatureControl } from '../components/editor/SignedControls';
import LifecycleActions from '../components/editor/LifecycleActions';
import VersionsPanel from '../components/editor/VersionsPanel';
import type { DocUpdater } from '../components/editor/types';
import { useToast } from '../components/Toast';
import SheetDialog from '../components/SheetDialog';
import ProposalManage from '../components/editor/ProposalManage';
import { embedContext, useTheme } from '../lib/theme';

const SECTIONS = [
  ['story', 'Hero, challenge & solution'], ['packages', 'Packages & add-ons'], ['commercial', 'Pricing extras & terms'],
  ['faq', 'FAQ'], ['testimonials', 'Testimonials'], ['timeline', 'Timeline'], ['video', 'Reference video'], ['links', 'Links'],
] as const;
type Section = typeof SECTIONS[number][0];

export default function AdminEditor() {
  const { id } = useParams<{ id: string }>();
  const [proposal, setProposal] = useState<AdminProposalDetail | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loadKey, setLoadKey] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setProposal(null);
    setLoadError('');
    adminApi
      .getProposal(id)
      .then((detail) => {
        if (!cancelled) setProposal(detail);
      })
      .catch((caught) => {
        if (cancelled) return;
        setLoadError(caught instanceof ApiError ? caught.message : 'Could not load this proposal.');
      });
    return () => {
      cancelled = true;
    };
  }, [id, loadKey]);

  return (
    <main className="min-h-screen bg-paper">
      {(!proposal || loadError) && (
        <div className="border-b border-line bg-white px-6 py-3">
          <Link to="/admin" className="text-sm font-medium text-ink-soft hover:text-ink">
            ← All proposals
          </Link>
        </div>
      )}

      {loadError && (
        <div className="mx-auto max-w-2xl px-6 py-16 text-center">
          <p className="text-error">{loadError}</p>
          <button
            type="button"
            onClick={() => setLoadKey((k) => k + 1)}
            className="mt-4 rounded-full border border-line px-4 py-1.5 text-sm font-medium text-ink-soft hover:border-ink-faint"
          >
            Try again
          </button>
        </div>
      )}

      {!loadError && !proposal && <p className="px-6 py-16 text-center font-mono text-sm text-ink-faint">Loading proposal…</p>}

      {!loadError && proposal && id && <EditorBody key={id} id={id} initial={proposal} />}
    </main>
  );
}

function EditorBody({ id, initial }: { id: string; initial: AdminProposalDetail }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [proposal, setProposal] = useState(initial);
  const [doc, setDoc] = useState<ProposalDocV1>(initial.doc);
  const [mode, setMode] = useState<'normal' | 'override'>('normal');
  const [conflict, setConflict] = useState<{ reason: string; error: string } | null>(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [mobileView, setMobileView] = useState<'form' | 'preview' | 'manage'>('form');
  const [section, setSection] = useState<Section>('packages');
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const previousSection = useRef('packages:form:false');
  const sectionHeading = useRef<HTMLHeadingElement>(null);
  const { theme, toggle } = useTheme();

  // Plain refs, not state: a save that's queued up behind an in-flight one
  // (see useAutosave's serialization) must read the value as of the moment
  // it actually runs, not whatever a React closure captured at render time.
  // Both change as a *result* of a save resolving (doc_version bumps;
  // overrideConfirm re-anchors to the just-saved client_name, U4), so a
  // follow-up save in the same drain loop needs the update immediately, not
  // on React's next render pass, which a `useState` value can't guarantee.
  const docVersionRef = useRef(initial.doc_version);
  const overrideConfirmRef = useRef<string | null>(null);

  function updateDocVersion(next: number) {
    docVersionRef.current = next;
  }

  function updateOverrideConfirm(next: string | null) {
    overrideConfirmRef.current = next;
  }

  const isLocked = proposal.status === 'signed' && mode === 'normal';
  const formDisabled = isLocked || conflict !== null;

  async function reloadProposal() {
    try {
      const detail = await adminApi.getProposal(id);
      setProposal(detail);
      setDoc(detail.doc);
      updateDocVersion(detail.doc_version);
      setMode('normal');
      updateOverrideConfirm(null);
      setConflict(null);
      adopt(detail.doc, detail.updated_at);
      setPreviewKey((k) => k + 1);
    } catch {
      // A failed reload leaves the current (possibly stale) state visible
      // rather than blanking the editor; the banner/error stays put.
    }
  }

  const save = useCallback(
    async (snapshot: ProposalDocV1) => {
      try {
        if (mode === 'override') {
          const result = await adminApi.overrideSaveProposal(id, {
            doc: snapshot,
            doc_version: docVersionRef.current,
            confirm: overrideConfirmRef.current ?? '',
          });
          if (result.ok) {
            updateDocVersion(result.doc_version);
            setProposal(current => ({ ...current, client_name: snapshot.client_name, doc_version: result.doc_version }));
            // The confirm string must track the doc's client_name, not the
            // name at the moment override mode was entered: if this save
            // renamed the client, every subsequent autosave still needs to
            // pass the *new* name to satisfy the server's confirm check.
            updateOverrideConfirm(snapshot.client_name);
            return { ok: true as const, doc_version: result.doc_version };
          }
          return result;
        }
        const result = await adminApi.saveProposal(id, { doc: snapshot, doc_version: docVersionRef.current });
        if (result.ok) {
          updateDocVersion(result.doc_version);
          setProposal(current => ({ ...current, client_name: snapshot.client_name, doc_version: result.doc_version }));
        }
        return result;
      } catch (caught) {
        // 400s (schema/structural validation) throw rather than resolve as a
        // conflict; fold the server's `details` into the message so the
        // save-status line has something readable instead of just "Schema
        // validation failed".
        if (caught instanceof ApiError && caught.details?.length) {
          throw new Error(`${caught.message}: ${caught.details.join('; ')}`);
        }
        throw caught;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, mode],
  );

  const { status: saveStatus, savedAt, errorMessage, flush, adopt, isDirty } = useAutosave({
    value: doc,
    save,
    enabled: !formDisabled,
    skipFirst: true,
    delayMs: 1500,
    onSaved: () => {
      setPreviewKey((k) => k + 1);
    },
    onConflict: (result) => {
      // A 'signed' conflict means someone else's signature landed mid-edit,
      // not a competing save: the doc this tab was editing no longer exists
      // in editable form. Refetch and let the normal signed-lock rendering
      // take over instead of showing the generic "someone else saved" banner.
      if (result.reason === 'signed') {
        showToast('error', result.error);
        void reloadProposal();
        return;
      }
      setConflict({ reason: result.reason, error: result.error });
      showToast('error', result.error);
    },
  });

  // Warn before losing edits that never made it to a save, whether the tab
  // is being closed while dirty or while a save is still in flight.
  useEffect(() => {
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      if (!isDirty && saveStatus !== 'saving') return;
      event.preventDefault();
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, saveStatus]);

  function updateDoc(updater: DocUpdater) {
    setDoc((current) => updater(current));
  }

  /** Flush pending edits and report whether it's safe to proceed. Every
   *  action that could otherwise race an unsaved edit (lifecycle actions,
   *  version restore, navigating away) calls this first and bails out if it
   *  comes back false, leaving whatever banner the flush produced in place. */
  const flushBeforeAction = useCallback(async () => {
    const result = await flush();
    return result === null || result.ok;
  }, [flush]);

  async function handleBackNavigation() {
    if (await flushBeforeAction()) navigate('/admin');
  }

  async function enterOverride() {
    await flush();
    updateOverrideConfirm(proposal.client_name);
    setMode('override');
  }

  async function stopOverride() {
    if (!(await flushBeforeAction())) return;
    setMode('normal');
    updateOverrideConfirm(null);
  }

  async function resetSignature(confirm: string) {
    if (!(await flushBeforeAction())) throw new Error('Save your changes successfully before resetting the signature.');
    await adminApi.resetSignature(id, confirm);
    await reloadProposal();
  }

  async function showPreview() {
    if (await flushBeforeAction()) setMobileView('preview');
  }
  function chooseSection(next: Section) {
    setSection(next); setMobileView('form'); setSectionsOpen(false);
  }
  useEffect(() => {
    const current = `${section}:${mobileView}:${sectionsOpen}`;
    if (current === previousSection.current) return;
    previousSection.current = current;
    if (mobileView === 'form' && !sectionsOpen && window.matchMedia('(max-width: 1023px)').matches) {
      sectionHeading.current?.focus({ preventScroll: true });
      sectionHeading.current?.scrollIntoView({ block: 'start' });
    }
  }, [section, mobileView, sectionsOpen]);
  const sectionClass = (name: Section) => `proposal-section-group space-y-5 ${section === name ? '' : 'hidden lg:block'}`;
  const sectionProps = { doc, onChange: updateDoc, disabled: formDisabled };

  return (
    <div className="proposal-editor flex min-h-dvh flex-col">
      <div className="editor-back border-b border-line bg-white px-6 py-3">
        <button
          type="button"
          onClick={handleBackNavigation}
          className="text-sm font-medium text-ink-soft hover:text-ink"
        >
          ← All proposals
        </button>
      </div>

      <header className="editor-heading border-b border-line bg-white px-6 py-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="font-display text-2xl text-ink">{doc.client_name || proposal.client_name}</h1>
              <StatusChip status={proposal.status} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-xs text-ink-faint">
              <span>/{proposal.slug}</span>
              <span className="hidden lg:inline"><SaveStatusLabel status={saveStatus} savedAt={savedAt} errorMessage={errorMessage} locked={isLocked} dirty={isDirty} override={mode === 'override'} /></span>
            </div>
          </div>

          <div className="hidden flex-wrap items-center gap-2 lg:flex">
            <button
              type="button"
              onClick={() => setVersionsOpen(true)}
              className="rounded-full border border-line px-4 py-1.5 text-sm font-medium text-ink-soft transition hover:border-ink-faint"
            >
              Versions
            </button>
            <LifecycleActions
              proposalId={id}
              slug={proposal.slug}
              status={proposal.status}
              onChanged={reloadProposal}
              flushPending={flushBeforeAction}
            />
          </div>
        </div>

        {conflict && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-warning/40 bg-warning-tint px-4 py-3 text-sm text-warning">
            <span>{conflict.error}</span>
            <button
              type="button"
              onClick={reloadProposal}
              className="rounded-full border border-warning px-3.5 py-1 text-xs font-semibold text-warning transition hover:bg-warning/10"
            >
              Reload
            </button>
          </div>
        )}

        {saveStatus === 'error' && errorMessage && (
          <div className="mt-4 rounded-xl border border-error/40 bg-error-tint px-4 py-3 text-sm text-error">
            {errorMessage}
          </div>
        )}

        {mode === 'override' && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gold bg-[#fbf6ec] px-4 py-3 text-sm text-[#6b4d24]">
            <span>Editing a signed proposal. Saves are going through the override path and are logged.</span>
            <button
              type="button"
              onClick={stopOverride}
              className="rounded-full border border-gold px-3.5 py-1 text-xs font-semibold text-[#6b4d24] transition hover:bg-gold/10"
            >
              Done editing
            </button>
          </div>
        )}

        {!embedContext.embedded && <button className="theme-toggle" type="button" onClick={toggle} aria-label={`Use ${theme === 'light' ? 'dark' : 'light'} theme`}>{theme === 'light' ? 'Dark' : 'Light'}</button>}
      </header>
      <div className="editor-toolbar lg:hidden">
        <div><span className="mono-label">Proposal editor</span><div className="mt-1 font-mono text-xs text-ink-faint" role="status"><SaveStatusLabel status={saveStatus} savedAt={savedAt} errorMessage={errorMessage} locked={isLocked} dirty={isDirty} override={mode === 'override'} /></div></div>
        <button className="pill-outline" type="button" onClick={() => mobileView === 'preview' ? setMobileView('form') : void showPreview()}>{mobileView === 'preview' ? 'Editor' : 'Preview'}</button>
      </div>

      <div className="editor-content grid flex-1 grid-cols-1 gap-6 px-6 py-6 lg:grid-cols-2">
        <div className={`space-y-5 ${mobileView !== 'form' ? 'hidden lg:block' : 'block'}`}>
          {isLocked && (
            <SignedLockBanner
              signedBy={proposal.signed_by}
              signedAt={proposal.signed_at}
              clientName={proposal.client_name}
              onEnterOverride={enterOverride}
            />
          )}

          <h2 ref={sectionHeading} tabIndex={-1} className="mobile-section-title mono-label text-ink-faint lg:hidden">{SECTIONS.find(([key]) => key === section)?.[1]}</h2>
          <div className={sectionClass('story')}><MobileStory {...sectionProps} /><div className="hidden space-y-5 lg:block"><HeroSection {...sectionProps} /><ChallengeSection {...sectionProps} /><SolutionSection {...sectionProps} /></div></div>
          <div className={sectionClass('packages')}><PackagesSection {...sectionProps} /></div>
          <div className={sectionClass('commercial')}><PricingSection {...sectionProps} /><TermsSection {...sectionProps} /></div>
          <div className={sectionClass('faq')}><FaqSection {...sectionProps} /></div>
          <div className={sectionClass('testimonials')}><TestimonialsSection {...sectionProps} /></div>
          <div className={sectionClass('timeline')}><TimelineSection {...sectionProps} /></div>
          <div className={sectionClass('video')}><ReferenceVideoSection {...sectionProps} /></div>
          <div className={sectionClass('links')}><LinksSection {...sectionProps} /></div>

          {proposal.status === 'signed' && (
            <div className="hidden lg:block"><ResetSignatureControl clientName={proposal.client_name} onReset={resetSignature} /></div>
          )}
        </div>

        <div className={`proposal-preview lg:sticky lg:top-6 lg:h-[calc(100vh-140px)] ${mobileView === 'preview' ? 'block' : 'hidden lg:block'}`}>
          <div className="h-full overflow-hidden rounded-2xl border border-line bg-white">
            <iframe
              key={previewKey}
              src={`/api/admin/proposals/${id}/preview`}
              title="Live preview"
              className="h-full w-full"
            />
          </div>
        </div>
      </div>

      {mobileView === 'manage' && <div className="mobile-manage lg:hidden">
        <ProposalManage proposal={proposal} flushPending={flushBeforeAction} onChanged={reloadProposal} onVersions={() => setVersionsOpen(true)}>
          {isLocked && <SignedLockBanner signedBy={proposal.signed_by} signedAt={proposal.signed_at} clientName={proposal.client_name} onEnterOverride={async () => { await enterOverride(); setMobileView('form'); }} />}
          {proposal.status === 'signed' && <ResetSignatureControl clientName={proposal.client_name} onReset={resetSignature} />}
        </ProposalManage>
      </div>}
      <nav className="editor-mobile-nav lg:hidden" aria-label="Proposal controls">
        <button type="button" aria-expanded={sectionsOpen} onClick={() => setSectionsOpen(true)}>Sections</button>
        <button type="button" aria-expanded={versionsOpen} onClick={() => setVersionsOpen(true)}>Versions</button>
        <button type="button" aria-pressed={mobileView === 'manage'} onClick={() => setMobileView('manage')}>Manage</button>
      </nav>
      <SheetDialog open={sectionsOpen} title="Proposal sections" onClose={() => setSectionsOpen(false)}>
        <div className="section-index">
          {SECTIONS.map(([key, label]) => <button type="button" key={key} aria-current={section === key && mobileView === 'form' ? 'true' : undefined} onClick={() => chooseSection(key)}>{label}<span aria-hidden="true">→</span></button>)}
          <button type="button" onClick={async () => { setSectionsOpen(false); await showPreview(); }}>Live preview<span aria-hidden="true">→</span></button>
          <button type="button" onClick={() => { setMobileView('manage'); setSectionsOpen(false); }}>Publication, sharing & signed controls<span aria-hidden="true">→</span></button>
        </div>
      </SheetDialog>
      <VersionsPanel
        proposalId={id}
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        onRestored={reloadProposal}
        flushPending={flushBeforeAction}
      />
    </div>
  );
}

function SaveStatusLabel({
  status,
  savedAt,
  errorMessage,
  locked,
  override,
  dirty,
}: {
  status: 'idle' | 'saving' | 'saved' | 'error' | 'stale';
  savedAt: number | null;
  errorMessage: string | null;
  locked: boolean;
  dirty: boolean;
  override: boolean;
}) {
  if (locked && !override) return <span>Read-only</span>;
  if (dirty && status !== 'saving' && status !== 'error' && status !== 'stale') return <span>Unsaved changes</span>;
  if (status === 'idle') return <span>Saved</span>;
  if (status === 'saving') return <span>Saving…</span>;
  if (status === 'saved') return <span>Saved {savedAt ? relativeTime(savedAt) : ''}</span>;
  if (status === 'stale') return <span className="text-warning">See the banner above</span>;
  return <span className="text-error" title={errorMessage ?? undefined}>Save failed</span>;
}
